import React, { useEffect, useState } from 'react';

import ScoringMatchEntry from './scoring-match-entry';
import ScoringMatchSides from './scoring-match-sides';
import { currentWinner, initialSets } from './scoring-match-draft';
import {
  opponentName,
  opponentRating,
  playerDeltaLabel,
  playerResultLabel,
} from './scoring-match-query';
import {
  deltaTone,
  formatDelta,
  smallPointsBalance,
  type MatchItem,
  type ScoringSystem,
  type StandingRow,
} from './scoring-types';

// Wpisywanie wyniku wymaga tokenu z linku organizatora. Publiczny podgląd kolejki
// go nie ma — wtedy mecz otwiera się tylko do odczytu.
interface EntryAccess {
  token: string;
  canSubmit: boolean;
  onSaved: () => void;
}

interface Props {
  player: StandingRow;
  matches: MatchItem[];
  showPoints: boolean;
  scoring?: ScoringSystem;
  entry?: EntryAccess;
  onClose: () => void;
}

interface StatProps {
  label: string;
  value: React.ReactNode;
}

const ModalStat: React.FC<StatProps> = ({ label, value }) => (
  <div className="scoring-modal__stat">
    <span className="scoring-modal__stat-label">{label}</span>
    <span className="scoring-modal__stat-value">{value}</span>
  </div>
);

export const ScoringPlayerMatchesModal: React.FC<Props> = ({
  player,
  matches,
  showPoints,
  scoring,
  entry,
  onClose,
}) => {
  const [pickedMatchId, setPickedMatchId] = useState<number | null>(null);

  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key === 'Escape') {
        onClose();
      }
    }

    document.addEventListener('keydown', onKeyDown);

    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const pickedMatch = matches.find((match) => match.id === pickedMatchId) ?? null;

  function backToList(): void {
    setPickedMatchId(null);
  }

  function handleSaved(): void {
    setPickedMatchId(null);
    entry?.onSaved();
  }

  return (
    <div className="scoring-modal__backdrop" data-testid="scoring-player-modal" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Mecze — ${player.name}`}
        className="scoring-modal"
        onClick={(event) => event.stopPropagation()}
      >
        <header className="scoring-modal__head">
          {pickedMatch !== null && (
            <button
              type="button"
              className="scoring-modal__back"
              data-testid="scoring-player-modal-back"
              onClick={backToList}
            >
              ← Mecze
            </button>
          )}
          <h3 className="scoring-modal__title">{player.name}</h3>
          <button
            type="button"
            className="scoring-modal__close"
            data-testid="scoring-player-modal-close"
            aria-label="Zamknij"
            onClick={onClose}
          >
            ✕
          </button>
        </header>

        {pickedMatch !== null ? (
          entry ? (
            <ScoringMatchEntry
              match={pickedMatch}
              token={entry.token}
              canSubmit={entry.canSubmit}
              scoring={scoring}
              onSaved={handleSaved}
              onCancel={backToList}
            />
          ) : (
            <div className="scoring-modal__entry" data-testid={`scoring-modal-entry-${pickedMatch.id}`}>
              <ScoringMatchSides
                match={pickedMatch}
                sets={initialSets(pickedMatch)}
                winner={currentWinner(pickedMatch)}
                scoring={scoring}
              />
            </div>
          )
        ) : (
          <>
            <p className="scoring-modal__rating" data-testid="scoring-player-modal-rating">
              <span className="scoring-modal__rating-value">{player.rating}</span>
              <span className={deltaTone(player.ratingDelta)}>
                {formatDelta(player.ratingDelta)}
              </span>
              <span className="scoring-modal__rating-note">ranking po tej kolejce</span>
            </p>

            <div className="scoring-modal__stats" data-testid="scoring-player-modal-stats">
              <ModalStat label="Miejsce" value={player.rank} />
              <ModalStat label="Mecze" value={`${player.wins}–${player.losses}`} />
              <ModalStat label="Sety" value={`${player.setsWon}:${player.setsLost}`} />
              <ModalStat
                label="Lotki"
                value={
                  <span className={deltaTone(smallPointsBalance(player))}>
                    {formatDelta(smallPointsBalance(player))}
                  </span>
                }
              />
              {showPoints && <ModalStat label="Pkt" value={player.points} />}
              <ModalStat label="Klas." value={player.placementPoints} />
            </div>

            {matches.length === 0 ? (
              <p className="scoring-modal__empty">Ten zawodnik nie ma meczów w tej grupie.</p>
            ) : (
              <ul className="scoring-modal__list">
                {matches.map((match) => (
                  <li key={match.id}>
                    <button
                      type="button"
                      className="scoring-modal__match"
                      data-testid={`scoring-player-modal-match-${match.id}`}
                      onClick={() => setPickedMatchId(match.id)}
                    >
                      <span className="scoring-modal__opponent">
                        vs {opponentName(match, player.leaguePlayerId)}
                        {opponentRating(match, player.leaguePlayerId) !== null && (
                          <span className="scoring-modal__opponent-rating">
                            {opponentRating(match, player.leaguePlayerId)}
                          </span>
                        )}
                      </span>
                      <span className="scoring-modal__result">
                        {playerResultLabel(match, player.leaguePlayerId)}
                      </span>
                      <span className="scoring-modal__delta">
                        {playerDeltaLabel(match, player.leaguePlayerId)}
                      </span>
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default ScoringPlayerMatchesModal;
